'use client';

import { useRef } from 'react';
import { useIntl } from 'react-intl';
import PageType from '@alltrails/amplitude/enums/PageType';
import logTopModuleSwiped from '@alltrails/amplitude/events/logTopModuleSwiped';
import useWindowSize from '@alltrails/core/hooks/useWindowSize';
import Button from '@alltrails/denali/components/Button';
import Carousel from '@alltrails/denali/components/Carousel';
import useIsMobileSizedScreen from '@alltrails/denali/hooks/useIsMobileSizedScreen';
import { LocationDetailsResponseType } from '@/types/locationDetailsPages/shared';
import { River, RiverItem } from '@/types/shared';
import { SubLocationDetailsResponseType } from '@/types/subLocationListPages/shared';
import { slidesPerViewBreakpoints, slidesPerViewMobile } from '../carouselConstants';
import styles from './TopModule.module.scss';
import TopModuleCard from './TopModuleCard';

type Props = {
  id: number;
  locationName?: string;
  locationType: LocationDetailsResponseType | SubLocationDetailsResponseType;
  pageType?: PageType;
  sectionId?: string;
  topModule: River;
};

const getTitleToRender = (intl, locationName, locationType, topModuleTitle) => {
  // break out locationName by locationType for better localization context
  // locationName will only be city, state, or country name
  if (locationType === 'country') {
    return intl.formatMessage({ defaultMessage: '{topParksTitle} in {countryName}' }, { topParksTitle: topModuleTitle, countryName: locationName });
  } else if (locationType === 'state') {
    return intl.formatMessage({ defaultMessage: '{topParksTitle} in {stateName}' }, { topParksTitle: topModuleTitle, stateName: locationName });
  } else if (locationType === 'city') {
    return intl.formatMessage({ defaultMessage: '{topParksTitle} in {cityName}' }, { topParksTitle: topModuleTitle, cityName: locationName });
  } else {
    return topModuleTitle;
  }
};

const TopModuleSection = ({ id, locationName, locationType, pageType, sectionId, topModule }: Props) => {
  const intl = useIntl();
  const carousel = useRef(false);
  const { width } = useWindowSize();
  const shouldHideOverflow = width >= 1920;

  const { items, type, last } = topModule;
  const titleToRender = locationName ? getTitleToRender(intl, locationName, locationType, topModule.title) : topModule.title;
  const isMobile = useIsMobileSizedScreen();

  return (
    <div className={styles.topModuleContainer} id={sectionId}>
      <div className={styles.titleAndButtonContainer}>
        <h2 className={styles.moduleTitle}>{titleToRender}</h2>
        {last && (
          <div className={styles.desktopShowMore}>
            <Button
              text={intl.formatMessage({ defaultMessage: 'Show all' })}
              linkInfo={{ href: last.url, target: '_blank' }}
            />
          </div>
        )}
      </div>
      <Carousel
        className={styles.carousel}
        slidesPerViewMobile={slidesPerViewMobile}
        slidesPerViewBreakpoints={slidesPerViewBreakpoints}
        uniqueId={`top-module-${type}`}
        items={items}
        showPaginationDots={false}
        showOverflow={!shouldHideOverflow}
        renderItem={(item: RiverItem) => <TopModuleCard item={item} id={id} pageType={pageType} />}
        getItemKey={(item: RiverItem) => item?.id?.toString()}
        cardAspectRatio={isMobile ? '3/4' : '2/3'}
        onSlideChange={() => {
          if (!carousel.current && pageType) {
            logTopModuleSwiped({ id: String(id), page_type: pageType });
            carousel.current = true;
          }
        }}
        ariaLabel={`${titleToRender} - ${intl.formatMessage({ defaultMessage: 'Carousel' })}`}
      />
      {last && (
        <div className={styles.mobileShowMore}>
          <Button
            text={intl.formatMessage({ defaultMessage: 'Show all' })}
            linkInfo={{ href: last.url, target: '_blank' }}
          />
        </div>
      )}
    </div>
  );
};

export default TopModuleSection;
